import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  SlidersHorizontal,
  X,
} from "lucide-react";

import Navbar from "../components/Navbar.jsx";
import Footer from "../components/Footer.jsx";
import CategoryCharts from "../components/CategoryCharts.jsx";
import api from "../lib/Urls";
import ListGrid from "../components/ListGrid";
import FilterBar from "../components/FilterBar";
import useMarketplaceFilters, {
  itemMatchesMarketplaceFilters,
} from "../hooks/useMarketplaceFilters";

const CategoryPage = () => {
  const { slug } = useParams();

  const [items, setItems] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const { filters } = useMarketplaceFilters();

  const normalizedSlug = useMemo(
    () => slug?.replace(/-/g, " ")?.toLowerCase(),
    [slug]
  );

  useEffect(() => {
    window.scrollTo(0, 500);
  }, [slug]);

  useEffect(() => {
    const cacheKey = "category_items_all";
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        setItems(JSON.parse(cached));
      } catch {
        sessionStorage.removeItem(cacheKey);
      }
    }

    api
      .get("/api/category-items")
      .then((response) => {
        const nextItems = Array.isArray(response.data)
          ? response.data
          : [];

        setItems(nextItems);

        sessionStorage.setItem(
          cacheKey,
          JSON.stringify(nextItems)
        );
      })
      .catch((error) =>
        console.error(
          "Failed to load category items",
          error
        )
      );
  }, []);

  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          item.categoryName?.toLowerCase() ===
            normalizedSlug &&
          itemMatchesMarketplaceFilters(
            item,
            filters
          )
      ),
    [items, normalizedSlug, filters]
  );

  const categoryName = useMemo(
    () =>
      slug
        ? slug
            .replace(/-/g, " ")
            .replace(
              /\b\w/g,
              (character) =>
                character.toUpperCase()
            )
        : "Category",
    [slug]
  );

  const hasActiveLocationFilter =
    Boolean(filters.country || filters.city);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <Link
          to="/"
          className="
            mb-6
            inline-flex
            items-center
            gap-2
            text-sm
            text-muted-foreground
            hover:text-foreground
          "
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Categories
        </Link>

        {/* ================================================ */}
        {/* CATEGORY HEADER + FILTER BUTTON */}
        {/* ================================================ */}

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-heading text-3xl font-bold sm:text-4xl">
              {categoryName}
            </h1>

            <p className="mt-2 text-sm text-muted-foreground">
              These are the items posted under{" "}
              {categoryName}
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              setShowFilters(
                (current) => !current
              )
            }
            aria-expanded={showFilters}
            className={`
              relative
              flex
              shrink-0
              items-center
              gap-2
              rounded-full
              border
              px-3.5
              py-2
              text-sm
              font-medium
              transition-all

              ${
                showFilters
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted"
              }
            `}
          >
            {showFilters ? (
              <X className="h-4 w-4" />
            ) : (
              <SlidersHorizontal className="h-4 w-4" />
            )}

            <span className="hidden sm:inline">
              {showFilters
                ? "Close"
                : "Filters"}
            </span>

            {!showFilters &&
              hasActiveLocationFilter && (
                <span
                  className="
                    absolute
                    -right-1
                    -top-1
                    h-2.5
                    w-2.5
                    rounded-full
                    bg-primary
                    ring-2
                    ring-background
                  "
                />
              )}
          </button>
        </div>

        {/* ================================================ */}
        {/* COLLAPSIBLE FILTER BAR */}
        {/* ================================================ */}

        {showFilters && (
          <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <FilterBar />
          </div>
        )}

        {/* ================================================ */}
        {/* ACTIVE FILTER SUMMARY */}
        {/* ================================================ */}

        {hasActiveLocationFilter && (
          <p className="mt-4 text-xs font-medium text-primary">
            Showing {filteredItems.length} matching item
            {filteredItems.length === 1
              ? ""
              : "s"}
            {filters.city
              ? ` in ${filters.city}`
              : ""}
            {filters.country
              ? `, ${filters.country}`
              : ""}
            .
          </p>
        )}

        {!hasActiveLocationFilter && (
          <div className="mb-9 mt-4" />
        )}

        {hasActiveLocationFilter && (
          <div className="mb-7" />
        )}

        <ListGrid
          items={filteredItems}
          slug={slug}
        />

        <CategoryCharts />
      </div>

      <Footer />
    </div>
  );
};

export default CategoryPage;
